/* globals ApplePaySession */
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useApolloClient } from '@apollo/client';
import PropTypes from 'prop-types';

import usePostHogFeatureFlag from 'hooks/usePostHogFeatureFlag';
import useUserData from 'data-hooks/useUserData';
import { logInfo } from 'commons/logger';

import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';

import {
  getApplePayData,
  getBraintreeClientToken,
} from 'components/consumer/checkout/withCheckoutUtils';
import classNames from 'classnames';
import placeOrderUtil from './placeOrderUtil';

const CheckoutWithApplePay = ({ checkoutSummary, orderTotal, products }) => {
  const postHogFlag = usePostHogFeatureFlag('applePayInCart');
  const [applePayData, setApplePayData] = useState(null);
  const [applePayLoading, setApplePayLoading] = useState(false);
  const shippingContact = useRef(null);
  const { email, firstName, lastName, isLoggedIn } = useUserData();
  const client = useApolloClient();

  /**
   *
   * Apple pay initiation
   *
   */
  const getApplePayInstance = useCallback(async () => {
    setApplePayLoading(true);

    const { error, clientToken } = await getBraintreeClientToken(
      {
        shipping: {
          first_name: firstName,
          last_name: lastName,
          quick_checkout_guest: !isLoggedIn,
        },
        email_address: email,
        orderSummary: {
          grand_total: orderTotal,
        },
        // is guest?
      },
      client
    );

    setApplePayLoading(false);
    if (error) {
      logInfo('loggr error', error);
    }

    const latestApplePayData = await getApplePayData(clientToken);
    logInfo('loggr obtained', clientToken);
    logInfo('loggr obtained', latestApplePayData);
    logInfo('loggr instance obtained', latestApplePayData.applePayInstance);

    setTimeout(() => {
      getApplePayData(clientToken).then(updatedApplePayData => {
        setApplePayData(updatedApplePayData);
      });
    });
  }, [client, email, firstName, isLoggedIn, lastName, orderTotal]);

  useEffect(() => {
    logInfo('loggr initiated');

    if (isLoggedIn) {
      logInfo('loggr initiated user');
      getApplePayInstance();
    }
  }, [getApplePayInstance, isLoggedIn]);

  useEffect(() => {
    logInfo('loggr products', products);
    logInfo('loggr orderTotal', orderTotal);
    logInfo('loggr checkoutSummary', checkoutSummary);
  });

  useEffect(() => {
    logInfo('loggr shipping contact updates', shippingContact);
  }, [shippingContact]);

  const showError = err => {
    logInfo('loggr err', err);
  };

  /**
   *
   * User Checkout
   *
   */

  const placeOrder = async (braintree_token, paymentEvent) => {
    const payment = {
      selected_type: 'apple_pay',
      braintree_token,
    };

    logInfo('loggr initiated placeorder', { payment, paymentEvent });

    const res = await placeOrderUtil(
      payment,
      paymentEvent,
      paymentEvent.billingContact,
      !isLoggedIn,
      client
    );

    logInfo('loggr placeOrder response', res);

    if (res?.orderId) {
      // await onOrderSuccess();
      logInfo('loggr successful order');
    }
  };

  const getNonce = () => {
    logInfo('loggr instance call');
    let errors;
    // const { grandTotal } = this.props;;
    const { applePayInstance } = applePayData;
    const paymentRequest = applePayInstance.createPaymentRequest({
      total: {
        label: 'Joybird',
        amount: parseFloat(orderTotal).toFixed(2),
      },
      requiredBillingContactFields: ['postalAddress', 'email', 'name', 'phone'],
    });

    setApplePayLoading(true);
    // need to use the updated sdk to use the new callback apis
    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = event => {
      logInfo('loggr merchant validation initiated', event);
      applePayInstance
        .performValidation({
          validationURL: event.validationURL,
          displayName: 'Joybird',
        })
        .then(merchantSession => {
          logInfo('loggr merchant validation successful', merchantSession);
          setApplePayLoading(false);
          session.completeMerchantValidation(merchantSession);
        })
        .catch(validationErr => {
          logInfo('loggr merchant validation failed', validationErr);
          // You should show an error to the user, e.g. 'Apple Pay failed to load.'
          setApplePayLoading(false);
          errors = [{ message: 'Error validating merchant:', validationErr }];
          session.abort();
          showError(errors);
          // return showError(errors);
        });
    };

    session.onpaymentauthorized = event => {
      logInfo('loggr payment authorized', event);
      applePayInstance
        .tokenize({
          token: event.payment.token,
        })
        .then(payload => {
          // Send payload.nonce to your server.
          const token = payload.nonce;
          // After you have transacted with the payload.nonce,
          // call `completePayment` to dismiss the Apple Pay sheet.
          setApplePayLoading(false);
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          placeOrder(token, payload);
        })
        .catch(tokenizeErr => {
          errors = [{ message: 'Error tokenizing Apple Pay:', tokenizeErr }];
          setApplePayLoading(false);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          showError(errors);
          // return showError(errors);
        });
    };

    session.begin();
  };

  if (postHogFlag === 'test') {
    return (
      <>
        <Helmet>
          <script src="https://js.braintreegateway.com/web/3.79.1/js/client.min.js" />
          <script src="https://js.braintreegateway.com/web/3.79.1/js/apple-pay.min.js" />
        </Helmet>

        <div className="checkout-with-apple-pay-container">
          <button
            type="submit"
            disabled={applePayLoading}
            data-xc="place-order-apple-pay-btn"
            className={classNames(
              'inline-block font-bold text-white text-base uppercase p-[1.1rem] bg-brand',
              'hover:bg-brand',
              `cta [--apple-pay-button-width:140px] [--apple-pay-button-height:30px] [--apple-pay-button-border-radius:5px] [--apple-pay-button-padding:5px_0px] ${
                applePayLoading ? 'cursor-not-allowed' : ''
              }`
            )}
            onClick={getNonce}
            style={{
              '-webkit-appearance': '-apple-pay-button',
              '-apple-pay-button-type': 'check-out',
              '-apple-pay-button-style': 'black',
              width: '20.25rem',
              padding: '20px',
              'margin-bottom': '20px',
              'border-radius': '4px',
            }}
          >
            {applePayLoading ? <ProgressLoader /> : ''}
          </button>
        </div>
      </>
    );
  }

  return null;
};

CheckoutWithApplePay.propTypes = {
  checkoutSummary: PropTypes.object,
  orderTotal: PropTypes.number,
  products: PropTypes.array,
};

export default CheckoutWithApplePay;
